// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import EyeView from "./EyeView/EyeView";
import TrialPicture from "src/components/containers/shared/ReportSections/ReportGraph/SynoptophoreGraph/StereopsisGraph/TrialReviewSection/TrialPicture";
import ShapesTrialPicture from "src/components/containers/shared/ReportSections/ReportGraph/SynoptophoreGraph/StereopsisGraph/TrialReviewSection/ShapesTrialPicture";

// Types
import {
  ExamSectionReportType,
  isAniseikoniaLiveStep,
} from "src/services/exams/types";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Utils
import {
  getActivityLabel,
  getDistanceLabel,
  getStepTitle,
} from "./synoptophoreLiveHelper";

// The trial pictures index the catch ("none stood out") answer after the four candidates.
const CATCH_INDEX = 4;
// No patient pick yet while the trial is live, so no response ring.
const NO_RESPONSE = -1;

type SynoptophoreType = {
  examData: ExamSectionReportType;
};

// The synoptophore's live view: what the patient is doing, what each eye is
// being shown and where, and, on a stereo trial, which target has the depth.
const Synoptophore = ({ examData }: SynoptophoreType) => {
  const { t } = useTranslation();

  // Both sections report their live step under this one key, so narrow before reading it:
  // an aniseikonia step has none of the fields below and belongs to its own panel.
  const currentStep = examData?.state?.currentStep;
  const step = isAniseikoniaLiveStep(currentStep) ? null : currentStep;

  const header = useMemo(
    () =>
      step
        ? {
            activity: getActivityLabel(step.activity, t),
            title: getStepTitle(step, t),
            distance: getDistanceLabel(step, t),
          }
        : null,
    [step, t]
  );

  if (!step || !header) {
    return (
      <Box
        sx={{
          width: "100%",
          padding: "8px",
          borderRadius: "4px",
          backgroundColor: "#5B6D7A20",
        }}
      >
        <Typography fontSize="12px">
          {t("Waiting for the next step to start.")}
        </Typography>
      </Box>
    );
  }

  const trial = step.stereoTrial;
  const expectedIndex = trial?.disparateCandidate ?? CATCH_INDEX;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
      >
        <Box display="flex" flexWrap="wrap" alignItems="center" gap="8px">
          <Typography component="span" fontSize="14px" fontWeight={600}>
            {header.title} {header.distance && `- ${header.distance.label}`}
          </Typography>
        </Box>

        <Typography
          component="span"
          fontSize="12px"
          sx={{
            padding: "4px 8px",
            borderRadius: "4px",
            backgroundColor: "#5B6D7A20",
            whiteSpace: "nowrap",
          }}
        >
          {header.activity}
        </Typography>
      </Box>

      {/* Left eye on the left, as the patient sees them */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          width: "100%",
          maxWidth: "612px",
          mx: "auto",
        }}
      >
        <EyeView eye={eyeTypeEnums.LEFT} step={step} />
        <EyeView eye={eyeTypeEnums.RIGHT} step={step} />
      </Box>

      {trial && (
        <Box
          component="figure"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            width: "100%",
            maxWidth: "612px",
            mx: "auto",
            my: 0,
          }}
        >
          <Typography component="figcaption" fontSize="12px" fontWeight={600}>
            {t("What the Patient is Choosing Between")}
          </Typography>
          <Box
            sx={{
              padding: "12px",
              borderRadius: "4px",
              backgroundColor: "#08090C",
            }}
          >
            <Box sx={{ maxWidth: "220px", mx: "auto" }}>
              {trial.shapes && trial.shapes.length > 0 ? (
                <ShapesTrialPicture
                  shapes={trial.shapes}
                  expectedIndex={expectedIndex}
                  responseIndex={NO_RESPONSE}
                  correct={false}
                />
              ) : (
                <TrialPicture
                  seed={trial.levelNumber}
                  expectedIndex={expectedIndex}
                  responseIndex={NO_RESPONSE}
                  correct={false}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Synoptophore;
